import Box from "@mui/material/Box";
import GlobalStyles from "@mui/material/GlobalStyles";
import Typography from "@mui/material/Typography";

import { StyledAnchor } from "@/components/common/styles";
import { ArrowIcon } from "@/components/marketing/MarketingIcons";
import MarketingShell from "@/components/marketing/MarketingShell";
import { HOME_HREF } from "@/components/marketing/header/navigation";
import { BODY_SMALL } from "@/components/marketing/marketingType";
import {
  ACCENT,
  FRAME,
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";

const CONTAINER = {
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  maxWidth: `${FRAME.header}px`,
  height: "100vh",
  mx: "auto",
  px: "16px",
};

const WRAP = {
  position: "relative",
  zIndex: 2,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "16px",
  [MID]: { textAlign: "center" },
};

const LIST = {
  position: "relative",
  display: "flex",
  alignItems: "center",
  [MID]: { width: "100%" },
};

const NUMERAL = {
  fontSize: "320px",
  fontWeight: 800,
  lineHeight: 1,
  opacity: 0.1,
  [MID]: { fontSize: "39vw" },
};

const COSMONAUT = {
  width: "240px",
  height: "240px",
  [MID]: { width: "30%", height: "auto" },
};

const LEAD = [BODY_SMALL, { textAlign: "center" }];

const BUTTON = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  // Column gap only: the published control leaves its row gap at `normal`.
  columnGap: "8px",
  width: "200px",
  maxWidth: "100%",
  height: "48px",
  px: "16px",
  borderRadius: "8px",
  backgroundColor: ACCENT,
  color: "common.black",
  fontSize: 16,
  lineHeight: "24px",
  whiteSpace: "nowrap",
  "&:hover": { backgroundColor: marketingPalette.accentHover },
  [NOT_WIDE]: { flex: "none" },
  [TINY]: { width: "100%" },
};

const LOGO_WRAP = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  pt: "32px",
};

/**
 * The vendored wordmark is cropped tighter than the published one, so the box
 * holds the published 192x32.95 and letterboxes it.
 */
const LOGO = {
  width: "192px",
  height: "32.9531px",
  maxWidth: "100%",
  objectFit: "contain",
};

/**
 * The published 404: one viewport tall on black, with neither header nor
 * footer. The ground goes on the document because the section is transparent
 * over it.
 */
function NotFoundSurface() {
  return (
    <>
      <GlobalStyles
        styles={{ body: { backgroundColor: "#000", minHeight: "100vh" } }}
      />
      <Box
        component="section"
        sx={{ position: "relative", color: "common.white" }}
      >
        <Box sx={CONTAINER}>
          <Box sx={WRAP}>
            <Box sx={LIST} aria-hidden>
              <Box sx={NUMERAL}>4</Box>
              {/* A Lottie on the published page; the box holds its width. */}
              <Box sx={COSMONAUT} />
              <Box sx={NUMERAL}>4</Box>
            </Box>
            <Typography variant="h3" component="h1">
              This is not the page you were looking for
            </Typography>
            <Typography component="p" sx={LEAD}>
              {"The page you are looking for doesn't exist or has been moved."}
            </Typography>
            <StyledAnchor href={HOME_HREF} sx={BUTTON}>
              Home Page
              <ArrowIcon />
            </StyledAnchor>
          </Box>
          <Box sx={LOGO_WRAP}>
            <Box
              component="img"
              src="/marketing-footer/webshare-wordmark-white.svg"
              alt="Webshare"
              sx={LOGO}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
}

/**
 * The page every unknown path on the site answers, at a 404 status: the
 * published site has one, the same under every section, with no section's
 * chrome around it. A section route draws it inside its layout, which draws
 * no chrome while a page below it is not found (`useNotFoundBelow`).
 */
export function NotFoundPage() {
  return (
    <>
      <title>Page not found | Webshare</title>
      <meta name="robots" content="noindex" />
      {/* The rest of `.error_body`, which holds because this is the whole
          document. */}
      <GlobalStyles
        styles={{
          body: { color: "#fff", display: "flex", flexDirection: "column" },
        }}
      />
      <NotFoundSurface />
    </>
  );
}

/** The same, for a route with no layout of its own to provide the shell. */
export default function LandingNotFound() {
  return (
    <MarketingShell>
      <NotFoundPage />
    </MarketingShell>
  );
}
